import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, ClockCounterClockwise, House, Barbell, UsersThree, Play } from '@phosphor-icons/react';
import { useActiveWorkout } from '../contexts/ActiveWorkoutContext';
import { WorkoutTimerPill } from './WorkoutTimer';

const NAV_ITEMS = [
  { path: '/profile',   icon: User,                  testId: 'nav-profile',   label: 'Profile' },
  { path: '/history',   icon: ClockCounterClockwise, testId: 'nav-history',   label: 'History' },
  { path: '/',          icon: House,                 testId: 'nav-home',      label: 'Home' },
  { path: '/exercises', icon: Barbell,               testId: 'nav-exercises', label: 'Exercises' },
  { path: '/crew',      icon: UsersThree,            testId: 'nav-crew',      label: 'Squad' },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeWorkout, refresh, elapsedSeconds } = useActiveWorkout();

  useEffect(() => { refresh(); }, [location.pathname, refresh]);

  const activeIdx = useMemo(() => {
    for (let i = 0; i < NAV_ITEMS.length; i += 1) {
      const p = NAV_ITEMS[i].path;
      if (p === '/' ? location.pathname === '/' : (location.pathname === p || location.pathname.startsWith(p + '/'))) {
        return i;
      }
    }
    return 2; // home fallback
  }, [location.pathname]);

  const showSessionBar =
    !!activeWorkout &&
    location.pathname !== '/' &&
    !location.pathname.startsWith('/session/');

  return (
    <>
      {showSessionBar && (
        <button
          onClick={() => navigate(`/session/${activeWorkout.id}`)}
          className="persistent-session-anchor active-workout-card z-40 flex items-center justify-between px-5 py-3 shadow-ios animate-slide-up rounded-ios-lg overflow-hidden"
          style={{
            bottom: 'calc(86px + env(safe-area-inset-bottom))',
            backgroundColor: '#C0392F',
            color: '#EDEAE3',
          }}
          data-testid="persistent-session-bar"
        >
          {/* Dripping droplets — see App.css .drip-* */}
          <span aria-hidden className="drip drip-1" />
          <span aria-hidden className="drip drip-2" />
          <span aria-hidden className="drip drip-3" />
          <span aria-hidden className="drip drip-4" />

          <div className="relative flex items-center gap-3 min-w-0">
            <span className="w-2 h-2 rounded-full bg-bone animate-pulse" />
            <div className="text-left min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/70 leading-none">
                Session Running
              </p>
              <p className="text-base font-black font-heading uppercase text-bone truncate mt-0.5 leading-none">
                Tap to resume
              </p>
            </div>
          </div>
          <div className="relative flex items-center gap-2 pl-3">
            <WorkoutTimerPill seconds={elapsedSeconds} tone="dark" testId="persistent-session-timer" />
            <Play size={18} weight="fill" />
          </div>
        </button>
      )}

      <nav className="bottom-nav" aria-label="Primary navigation">
        {/* Glass puck slides between icons */}
        <span
          aria-hidden
          className="nav-puck"
          style={{ '--nav-idx': activeIdx }}
          data-testid="nav-puck"
        />
        {NAV_ITEMS.map((item, idx) => {
          const isActive = idx === activeIdx;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`bottom-nav-item icon-only ${isActive ? 'active' : ''}`}
              data-testid={item.testId}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon size={26} weight={isActive ? 'fill' : 'regular'} />
              <span className="nav-dot" />
            </button>
          );
        })}
      </nav>
    </>
  );
}
